import { useCallback, useMemo, useState } from 'react';
import useSceneThumbs from '../../hooks/useSceneThumbs';
import BattlefieldSceneRail from '../BattlefieldSceneRail';
import LibraryPicker from '../assets/LibraryPicker';
import { isNewer, ownedRow } from '../../engine/assets/provenance';
import SceneCutDialog from '../SceneCutDialog';
import { BATTLEFIELD_PRESETS, buildPreset } from '../../engine/battlefield/presets';
import { diag, errorSummary } from '../../diagnostics/log';

/** Presets and the session library, as the NEW SCENE dialog's map list. */
function sceneMapsOf(library) {
  return [
    ...BATTLEFIELD_PRESETS.map((p) => ({ key: p.key, label: p.label })),
    ...library.map((entry) => ({ key: `saved:${entry.name}`, label: entry.name })),
  ];
}

/** The terrain behind a map-list key: a saved map by name, or a preset built fresh. */
function sceneTerrainOf(key, library) {
  if (!key.startsWith('saved:')) return buildPreset(key);
  return library.find((e) => e.name === key.slice(6))?.terrain ?? null;
}

/** Ask the GM for a name; the trimmed answer, or null for none. */
function askName(question, suggestion) {
  const name = window.prompt(question, suggestion);
  return name?.trim() ? name.trim() : null;
}

/**
 * The rail's verbs. Each is the hook's; the panel only asks the questions -
 * a name, a confirmation - and shows the sentence a verb came back with.
 */
function railActions(scenes, stage, { setDialog, report }) {
  const find = (sceneId) => scenes.scenes.find((x) => x.id === sceneId);
  return {
    onCutTo: (sceneId) => setDialog({ kind: 'cut', sceneId }),
    onNew: () => setDialog({ kind: 'new' }),
    onCutBack: () => stage?.previousSceneId && setDialog({ kind: 'cut', sceneId: stage.previousSceneId }),
    onStrike: () => {
      const question = stage?.sceneId ? 'Rename the scene on stage' : 'Name this scene';
      const name = askName(question, stage?.name || `Scene ${scenes.scenes.length + 1}`);
      if (name) report(scenes.strike(name));
    },
    onCurtain: scenes.setCurtain,
    onRename: (sceneId) => {
      const name = askName('Rename the scene', find(sceneId)?.name ?? '');
      if (name) scenes.rename(sceneId, name);
    },
    onDuplicate: (sceneId) => report(scenes.duplicate(sceneId)),
    onReset: (sceneId) => report(scenes.reset(sceneId)),
    onDelete: (sceneId) => {
      const current = find(sceneId);
      // Asks only when somebody is parked inside - the rooms' rule.
      const asks = current?.castIds.length;
      if (asks && !window.confirm(`${current.name} has ${current.castIds.length} in it. Delete it?`)) return;
      report(scenes.remove(sceneId));
    },
    onExport: (sceneId) => report(scenes.exportScene(sceneId)),
    onImport: (file) =>
      report(
        file
          .text()
          .then((text) => scenes.importSceneFile(JSON.parse(text)))
          .catch((err) => `Could not read that file: ${err?.message ?? err}`),
      ),
  };
}

const said = (err) => err?.message ?? String(err);

/** Each picked row read in turn: the copies that read (with their origin), and "name (why)" for each that did not. */
async function readPicked(rows, readScene) {
  const read = await Promise.allSettled(rows.map((row) => readScene(row)));
  const copies = [];
  const bad = [];
  read.forEach((result, i) => {
    const row = rows[i];
    if (result.status === 'fulfilled') {
      copies.push({
        parked: result.value,
        origin: { assetId: row.id, version: row.version },
        label: `${row.name} v${row.version}`,
      });
      return;
    }
    diag.warn('scenes', 'a library scene could not be read', errorSummary(result.reason));
    bad.push(`${row.name} (${said(result.reason)})`);
  });
  return { copies, bad };
}

/** The status line after a copy: what landed, then what did not. */
function copiedSentence(copies, bad) {
  const landed = copies.length ? `${copies.map((c) => c.label).join(', ')} copied into the wings.` : '';
  const missed = bad.length ? `Not copied: ${bad.join(', ')}.` : '';
  return [landed, missed].filter(Boolean).join(' ');
}

/**
 * The GM's library in the wings (plan assets-library, stage 2): scene assets COPIED in - several at once, the user's
 * 2026-10-20 - each with its origin; a copy brought up to the library's newer version; and a scene saved to the
 * library as its next version. Each returns the sentence for the status line; a library that will not read says its
 * own sentence.
 */
function libraryActions(scenes, tableAssets, setPicking) {
  if (!tableAssets) return {};
  const copyIn = async (rows) => {
    const { copies, bad } = await readPicked(rows, tableAssets.readScene);
    const refusal = copies.length ? await scenes.parkFromLibrary(copies).catch(said) : null;
    return refusal ?? copiedSentence(copies, bad);
  };
  const update = (sceneId) => {
    const scene = scenes.scenes.find((x) => x.id === sceneId);
    const row = ownedRow(tableAssets.lib.assets, scene?.origin);
    if (!row) return Promise.resolve(`${scene?.name ?? 'That scene'} is no longer in your library.`);
    return tableAssets
      .readScene(row)
      .then((parked) => scenes.replaceFromLibrary(sceneId, parked, { assetId: row.id, version: row.version }))
      .then((refusal) => refusal ?? `${scene.name} is now v${row.version}.`, said);
  };
  const newerOf = (scene) =>
    isNewer(tableAssets.lib.assets, scene.origin) ? ownedRow(tableAssets.lib.assets, scene.origin).version : null;
  // Its toast says it landed; the status line is for a refusal or a library that would not take it.
  const save = (sceneId) => scenes.saveToLibrary(sceneId, tableAssets.saveScene).then((refusal) => refusal, said);
  return { onFromLibrary: () => setPicking(true), copyIn, update, newerOf, save };
}

/** The library's props on the rail: absent without a library, so the rail shows none of its verbs. */
function railLibrary(fromLibrary, report) {
  if (!fromLibrary.save) return {};
  return {
    onFromLibrary: fromLibrary.onFromLibrary,
    newerOf: fromLibrary.newerOf,
    onUpdateFromLibrary: (id) => report(fromLibrary.update(id)),
    onSaveToLibrary: (id) => report(fromLibrary.save(id)),
  };
}

const copyLabel = (n) => (n ? `Copy ${n} into the wings` : 'Copy into the wings');

/** From my library · Scenes: several at once; the picker closes, then the copies are made. */
function ScenePicker({ lib, uid, scenes, onClose, onPick }) {
  return (
    <LibraryPicker
      lib={lib}
      uid={uid}
      kind="scene"
      title="From my library · Scenes"
      action="Copy into the wings"
      copies={scenes}
      onClose={onClose}
      onPick={(row) => {
        onClose();
        onPick([row]);
      }}
      many={{
        label: copyLabel,
        onPick: (rows) => {
          onClose();
          onPick(rows);
        },
      }}
    />
  );
}

/**
 * The dialog's answer: a cut to a parked scene, or a new scene on a chosen
 * map. The dialog closes first, so a slow verb never leaves it hanging.
 */
function confirmCut(dialog, choice, { scenes, sceneMaps, library, setDialog, setStatus, report }) {
  const { travellers, parkAs, name, mapKey } = choice;
  setDialog(null);
  if (dialog.kind === 'cut') return report(scenes.cutTo(dialog.sceneId, travellers));
  const terrain = sceneTerrainOf(mapKey, library);
  if (!terrain) return setStatus('That map could not be loaded.');
  const mapName = sceneMaps.find((m) => m.key === mapKey)?.label ?? null;
  return report(scenes.newScene({ terrain, mapName, name, travellers, parkAs }));
}

/**
 * The rail's status line. Every verb's outcome lands on it. Nobody awaits
 * `report`, so a verb that throws (the server unreachable) is said there and
 * logged here - never an uncaught rejection (2026-10-02).
 */
function useStatusLine() {
  const [status, setStatus] = useState(null);
  const report = useCallback(async (outcome) => {
    try {
      const result = await outcome;
      setStatus(typeof result === 'string' ? result : (result?.error ?? null));
    } catch (err) {
      diag.warn('scenes', 'scene change failed', errorSummary(err));
      setStatus(`The scene change did not reach the server: ${errorSummary(err).message}`);
    }
  }, []);
  return { status, setStatus, report };
}

/**
 * Scenes mode's rail and the cut dialog.
 *
 * The dialog is `{ kind: 'cut', sceneId }` or `{ kind: 'new' }`: who
 * travels, which map, what to park the stage as. It is held here and shown
 * whatever the mode, as it always was; the rail only in Scenes mode.
 */
export default function PanelScenes({ panel, map }) {
  const { scenes, stage, roster, library, tableAssets } = panel;
  const [dialog, setDialog] = useState(null);
  const [picking, setPicking] = useState(false);
  const { status, setStatus, report } = useStatusLine();
  const sceneMaps = useMemo(() => sceneMapsOf(library), [library]);
  // Each card's set, for its wireframe thumbnail (plan map-display, I).
  const thumbOf = useSceneThumbs({
    scenes: scenes?.scenes,
    onStageId: stage?.sceneId ?? null,
    stageSolids: map.solids,
    stageBoundary: map.boundary,
    read: panel.readSceneSet,
  });
  if (!scenes) return null;
  const nextName = `Scene ${scenes.scenes.length + 1}`;
  const fromLibrary = libraryActions(scenes, tableAssets, setPicking);
  return (
    <>
      {map.scenesMode && (
        <BattlefieldSceneRail
          thumbOf={thumbOf}
          scenes={scenes.scenes}
          onStageId={stage?.sceneId ?? null}
          stageSummary={scenes.stageSummary}
          stageName={stage?.name ?? ''}
          curtain={!!stage?.curtain}
          canCutBack={!!stage?.previousSceneId}
          busy={scenes.busy}
          status={status}
          {...railActions(scenes, stage, { setDialog, report })}
          {...railLibrary(fromLibrary, report)}
        />
      )}
      {picking && tableAssets && (
        <ScenePicker
          lib={tableAssets.lib}
          uid={tableAssets.uid}
          scenes={scenes.scenes}
          onClose={() => setPicking(false)}
          onPick={(rows) => report(fromLibrary.copyIn(rows))}
        />
      )}
      {dialog && (
        <SceneCutDialog
          kind={dialog.kind}
          targetName={scenes.scenes.find((x) => x.id === dialog.sceneId)?.name ?? ''}
          roster={roster}
          defaults={scenes.travellerDefaults}
          refusal={(travellers) => (dialog.kind === 'cut' ? scenes.canCut(dialog.sceneId, travellers) : null)}
          needsParkName={!stage?.sceneId && roster.length > 0}
          suggestedParkName={nextName}
          maps={sceneMaps}
          onClose={() => setDialog(null)}
          onConfirm={(choice) =>
            confirmCut(dialog, choice, { scenes, sceneMaps, library, setDialog, setStatus, report })
          }
        />
      )}
    </>
  );
}
